import { useRef, useState } from 'react'
import { UploadCloud } from 'lucide-react'
import {
  useImportCatalogStatus,
  useSetAllCatalogStatus,
  useUpdateCatalogStatus,
} from '@/hooks/useProducts'
import {
  STATUS_HEADERS,
  parseCatalogStatusCsv,
  parseCatalogStatusWorkbook,
  statusLabel,
} from '@/lib/productCodeStatus'
import type { ProductCatalogFilters, ProductCatalogRow } from '@/types/products'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

// ─── Product code Active / Inactive (z8nrz7e72t) ─────────────────────────────
//
// Every control here changes only the selected tenant's Active and Note on a
// code. The code's ISBT columns are global reference data and stay read-only.

/** The longest note the server accepts. */
const NOTE_MAX_LENGTH = 1000

/**
 * Edit one code: Active, and the note saying why. Nothing else is editable.
 *
 * `flip` opens it with Active already set to the opposite of the saved value —
 * the row menu's "Change status", where the point is to confirm the change and
 * say why.
 */
export function EditCatalogStatusDialog({
  entry,
  flip = false,
  onClose,
}: {
  entry: ProductCatalogRow | null
  flip?: boolean
  onClose: () => void
}) {
  return (
    <Dialog open={!!entry} onOpenChange={next => !next && onClose()}>
      <DialogContent className='sm:max-w-md'>
        {/* Keyed so each opening starts from that row's saved values. */}
        {entry && <EditCatalogStatusForm key={entry.id} entry={entry} flip={flip} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

function EditCatalogStatusForm({
  entry,
  flip,
  onClose,
}: {
  entry: ProductCatalogRow
  flip: boolean
  onClose: () => void
}) {
  const update = useUpdateCatalogStatus()
  const [active, setActive] = useState(flip ? !entry.active : entry.active)
  const [note, setNote] = useState(entry.note)
  const unchanged = active === entry.active && note.trim() === entry.note

  return (
    <>
      <DialogHeader>
        <DialogTitle className='font-mono'>{entry.productDescriptionCode}</DialogTitle>
        <DialogDescription className='break-words font-mono text-xs'>{entry.description}</DialogDescription>
      </DialogHeader>

      <label className='flex items-center justify-between gap-3 rounded-md border px-3 py-2'>
        <span className='text-sm font-medium'>{statusLabel(active)}</span>
        <Switch checked={active} onCheckedChange={setActive} aria-label='Active' />
      </label>

      <div className='space-y-1'>
        <label className='text-xs font-medium text-muted-foreground' htmlFor='catalog-status-note'>
          Note
        </label>
        <Textarea
          id='catalog-status-note'
          value={note}
          maxLength={NOTE_MAX_LENGTH}
          onChange={e => setNote(e.target.value)}
          placeholder='Why this code is active or inactive'
          rows={3}
        />
      </div>

      {entry.statusUpdatedBy && entry.statusUpdatedAt && (
        <p className='text-xs text-muted-foreground'>
          Last set by {entry.statusUpdatedBy} on {new Date(entry.statusUpdatedAt).toLocaleString()}
        </p>
      )}
      {update.error instanceof Error && <p className='text-sm text-destructive'>{update.error.message}</p>}

      <div className='flex justify-end gap-2'>
        <Button variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={unchanged || update.isPending}
          onClick={() =>
            update.mutate({ catalogEntryIds: [entry.id], active, note }, { onSuccess: onClose })
          }
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </>
  )
}

/**
 * Activate ALL / Inactivate ALL, confirmed first. "All" is every code the
 * current filters match — the whole catalog when nothing is filtered — and the
 * dialog says how many that is before anything is written.
 */
export function SetAllCatalogStatusDialog({
  active,
  filters,
  total,
  filtered,
  onClose,
}: {
  /** null while closed. */
  active: boolean | null
  filters: ProductCatalogFilters
  total: number
  /** Whether any filter narrows the table — decides how the scope is worded. */
  filtered: boolean
  onClose: () => void
}) {
  const setAll = useSetAllCatalogStatus()
  const verb = active ? 'Activate' : 'Inactivate'

  function handleOpenChange(next: boolean) {
    if (!next) {
      setAll.reset()
      onClose()
    }
  }

  return (
    <Dialog open={active !== null} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{verb} all</DialogTitle>
          <DialogDescription>
            {verb} {total.toLocaleString()} code{total === 1 ? '' : 's'}
            {filtered ? ' — every code matching the current filters, on every page' : ' — the whole catalog'}
            , in this tenant only. Notes are left as they are.
          </DialogDescription>
        </DialogHeader>

        {setAll.error instanceof Error && <p className='text-sm text-destructive'>{setAll.error.message}</p>}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={active ? 'default' : 'destructive'}
            disabled={setAll.isPending || total === 0 || active === null}
            onClick={() =>
              active !== null && setAll.mutate({ filters, active }, { onSuccess: () => handleOpenChange(false) })
            }
          >
            {setAll.isPending ? 'Saving…' : `${verb} ${total.toLocaleString()}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The Excel upload: Product_Code, Active and Note per row, nothing else.
 *
 * Built to read the Export back — edit the Active and Note columns and upload
 * the same file. Every other column in it is ignored, so an edited description
 * cannot reach the catalog. See src/lib/productCodeStatus.ts for the parser.
 */
export function UploadCatalogStatusDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const upload = useImportCatalogStatus()
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const result = upload.data
  const { progress } = upload
  const busy = parsing || upload.isPending

  function handleOpenChange(next: boolean) {
    if (!next) {
      setFile(null)
      setError(null)
      upload.reset()
      onClose()
    }
  }

  async function handleUpload() {
    if (!file) return
    setParsing(true)
    setError(null)
    try {
      const { rows, errors } = file.name.toLowerCase().endsWith('.csv')
        ? parseCatalogStatusCsv(await file.text())
        : await parseCatalogStatusWorkbook(await file.arrayBuffer())
      // All or nothing: half a sheet applied is harder to reason about than
      // none of it, and every error names its row.
      if (errors.length > 0) {
        setError(errors.join(' '))
        return
      }
      if (rows.length === 0) {
        setError('No product codes found in the file.')
        return
      }
      upload.mutate(rows, { onSuccess: () => setFile(null) })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.')
    } finally {
      setParsing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Active / Note</DialogTitle>
          <DialogDescription>
            Upload an edited Export to set each code's {STATUS_HEADERS.active} and {STATUS_HEADERS.note}{' '}
            in this tenant. Only those two columns are read — every other column is ignored.
          </DialogDescription>
        </DialogHeader>

        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          onDragOver={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDrop={e => {
            e.preventDefault()
            e.stopPropagation()
            const dropped = e.dataTransfer.files?.[0]
            if (dropped) setFile(dropped)
          }}
          className='flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-6 py-10 text-center transition-colors hover:bg-muted/40'
        >
          <UploadCloud className='h-8 w-8 text-muted-foreground' />
          <p className='text-sm font-medium'>{file?.name ?? 'Drag file here or click to upload'}</p>
          <p className='text-xs text-muted-foreground'>
            CSV or Excel. Needs {STATUS_HEADERS.code}, plus {STATUS_HEADERS.active} (Yes / No) and/or{' '}
            {STATUS_HEADERS.note}. A blank Active cell leaves that code's status as it is; a blank Note
            cell clears its note.
          </p>
        </button>
        <input
          ref={inputRef}
          type='file'
          accept='.csv,.xlsx,.xls'
          className='hidden'
          onChange={e => setFile(e.target.files?.[0] ?? null)}
        />

        {error && <p className='text-sm text-destructive'>{error}</p>}
        {upload.error instanceof Error && <p className='text-sm text-destructive'>{upload.error.message}</p>}
        {upload.isPending && progress.total > 0 && (
          <p className='text-xs text-muted-foreground'>
            {progress.done.toLocaleString()} of {progress.total.toLocaleString()} rows sent…
          </p>
        )}
        {result && (
          <div className='space-y-1 text-sm text-muted-foreground'>
            <p>
              Updated {result.updated.toLocaleString()} code{result.updated === 1 ? '' : 's'}.
            </p>
            {result.unknownCodes.length > 0 && (
              <p className='text-destructive'>
                Not in the catalog, skipped: {result.unknownCodes.slice(0, 20).join(', ')}
                {result.unknownCodes.length > 20 ? ` and ${result.unknownCodes.length - 20} more` : ''}.
              </p>
            )}
          </div>
        )}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' disabled={busy} onClick={() => handleOpenChange(false)}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          <Button disabled={!file || busy} onClick={handleUpload}>
            {parsing ? 'Reading file…' : upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
